import { PurposeModel, stackIds, StackModel, stackPurposesMapping } from './PreferenceModel';
import { Stack } from './Stack';
import { getText } from '../../TranslationService';
import { VendorsLink } from '../shared/VendorsLink';
import { Heading } from '../shared/Heading';
import { Description } from '../shared/Description';
import { ScrollableContent } from '../shared/ScrollableContent';
import { Grid } from '../shared/Grid';
import { ListOptionState } from '../shared/UiStates/TriStateCheckbox';
import { UsePreferenceSelections } from '../shared/UiStates/UsePreferenceSelections';

const defaultPurposes: Readonly<number[]> = [];

const Note = () => {
  return (
    <Grid direction={'vertical'}>
      <p>{getText(`footer_note`)}</p>
      <Grid direction={'horizontal'}>
        <VendorsLink translationPrefix="preferences" />
        <div class="ccm-non-IAB-note">{getText(`IAB_note`)}</div>
      </Grid>
    </Grid>
  );
};

export const Content = () => {
  const { preferenceSelections, setPreferenceSelections } = UsePreferenceSelections();

  function isPurposeSelected(id: number) {
    return !!preferenceSelections.filter((purposeId) => purposeId === id)[0];
  }

  function unselectPurposes(...ids: number[]): number[] {
    const selectedPurposes: number[] = [...preferenceSelections];

    for (const id of ids) {
      const index = selectedPurposes.indexOf(id);
      if (index >= 0) selectedPurposes.splice(index, 1);
    }

    return selectedPurposes;
  }

  function selectPurposes(...ids: number[]): number[] {
    const selectedPurposes: number[] = [...preferenceSelections];
    for (const id of ids) {
      if (!isPurposeSelected(id)) selectedPurposes.push(id);
    }
    return selectedPurposes;
  }

  function onStackToggle(stackStatus: ListOptionState, ...purposeIds: number[]): number[] {
    switch (stackStatus) {
      case ListOptionState.all:
        return unselectPurposes(...purposeIds);
      case ListOptionState.some:
        return unselectPurposes(...purposeIds);
      case ListOptionState.none:
        return selectPurposes(...purposeIds);
    }
  }

  function setSelectedConsentState(selectedPurposeIds: number[]) {
    const isControlPurposeSelected = selectedPurposeIds.filter((purposeID) => purposeID === 1).length === 1;
    if (!isControlPurposeSelected) selectedPurposeIds = [...defaultPurposes];
    setPreferenceSelections(selectedPurposeIds);
  }

  function isControlStackEnabled(): boolean {
    return getStackToggleState(stackIds.STORAGE) === ListOptionState.all;
  }

  const getStackToggleState = (stackId: number): ListOptionState => {
    const stackPurposeIds = stackPurposesMapping[stackId];
    const stackSelectedPurposeIds = preferenceSelections.filter((spId) => stackPurposeIds.indexOf(spId) >= 0);

    switch (stackSelectedPurposeIds.length) {
      case 0:
        return ListOptionState.none;
      case stackPurposeIds.length:
        return ListOptionState.all;
      default:
        return ListOptionState.some;
    }
  };

  const onPurposeChangeHandler = (id: number, isOptedIn: boolean): void => {
    const selectedPurposeIds = isOptedIn ? selectPurposes(id) : unselectPurposes(id);

    setSelectedConsentState(selectedPurposeIds);
  };

  const onStackChangeHandler = (id: number): void => {
    const purposeIds = stackPurposesMapping[id];

    const stackStatus = getStackToggleState(id);

    const selectedPurposeIds = onStackToggle(stackStatus, ...purposeIds);

    setSelectedConsentState(selectedPurposeIds);
  };

  function getStorageStackModel() {
    return new StackModel({
      id: stackIds.STORAGE,
      name: 'storage',
      ToggleState: getStackToggleState(stackIds.STORAGE),
      shouldDisplayDescription: false,
      Purposes: stackPurposesMapping[stackIds.STORAGE].map(
        (id) =>
          new PurposeModel({
            id: id,
            name: id.toString(),
            optedIn: isPurposeSelected(id),
            shouldDisplayTitle: false,
          }),
      ),
    });
  }

  function getPersonlisedAdStackModel() {
    return new StackModel({
      id: stackIds.PERSONALISED_AD,
      name: 'ads',
      ToggleState: getStackToggleState(stackIds.PERSONALISED_AD),
      isStackEnabled: isControlStackEnabled(),
      Purposes: stackPurposesMapping[stackIds.PERSONALISED_AD].map(
        (id) => new PurposeModel({ id: id, name: id.toString(), optedIn: isPurposeSelected(id) }),
      ),
    });
  }

  function getThirdPartyStackModel() {
    return new StackModel({
      id: stackIds.THIRD_PARTY,
      name: 'third_party',
      ToggleState: getStackToggleState(stackIds.THIRD_PARTY),
      shouldDisplayDescription: false,
      isStackEnabled: isControlStackEnabled(),
      Purposes: stackPurposesMapping[stackIds.THIRD_PARTY].map(
        (id) =>
          new PurposeModel({
            id: id,
            name: 'third_party',
            optedIn: isPurposeSelected(id),
            shouldDisplayTitle: false,
            shouldDisplayLegalDescription: false,
          }),
      ),
    });
  }
  const stackHandlers = {
    onPurposeChangeHandler: onPurposeChangeHandler,
    onStackChangeHandler: onStackChangeHandler,
  };
  return (
    <ScrollableContent>
      <Heading />
      <Description textTranslationKey={'pref_message'} />
      <section class="stacks-and-purposes">
        <Stack stack={getStorageStackModel()} {...stackHandlers} />
        <Stack stack={getPersonlisedAdStackModel()} {...stackHandlers} />
        <Stack stack={getThirdPartyStackModel()} {...stackHandlers} />
      </section>
      <Note />
    </ScrollableContent>
  );
};
